import React, { useState } from 'react';
import {
  TableContextProvider,
  TableFooter,
  TablePaginationNew,
  TablePaginationSizeNew,
  Table,
} from 'lib/components';
import { Gap } from 'lib/components/styles/layout';
import { MdDeleteForever } from 'react-icons/md';
import { Button } from 'react-covideo-common';
import { PaginationConstants } from 'lib/const/PaginationConstants';
import { SortButtons } from 'app/pages/admin/components/SortButtons';
import {
  Layout,
  NormalText,
  PaginationWrapper,
  TableCell,
} from 'app/pages/admin/index.styled';
import { DeletePronunciationModal } from '../modal/DeletePronunciationModal';
import {
  PRONUNCIATIONS_SORT_KEYS,
  PronunciationsTableColumns,
  getTableFields,
} from '../constants';
import { IPronunciation } from 'lib/api/pronunciations/useQueryPronunciations';
import { useTheme } from 'styled-components/macro';
import { useAudioPlayer } from 'lib/hooks/useAudioPlayer';
import { AudioPlayerIcon } from 'lib/components/audio/AudioPlayerIcon';
import { useTranslation } from 'react-i18next';

type Props = {
  count: number;
  data: IPronunciation[];
  page: number;
  size: number;
  onPaginationChange({ page, size }: { page: number; size: number }): void;
  onSortingUpdate(sortKey: string, order: PaginationConstants): void;
  sortKey: PRONUNCIATIONS_SORT_KEYS | null;
  order?: PaginationConstants.ASCENDING | PaginationConstants.DESCENDING;
};

export const PronunciationTable = ({
  count,
  data,
  page,
  size,
  onPaginationChange,
  onSortingUpdate,
  order,
  sortKey,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [pronunciationToDelete, setPronunciationToDelete] =
    useState<IPronunciation | null>(null);

  const { playingId, toggleAudio } = useAudioPlayer<number>();

  const tableFields = getTableFields(t);
  return (
    <Layout>
      {pronunciationToDelete && (
        <DeletePronunciationModal
          pronunciation={pronunciationToDelete}
          onClose={() => setPronunciationToDelete(null)}
        />
      )}
      <TableContextProvider
        total={count}
        onChange={onPaginationChange}
        initPage={page}
        initSize={size}
      >
        <div style={{ paddingBottom: '20px' }}>
          <Table
            compact={true}
            borderColor={theme.colors.neutral[10]}
            relative={true}
            headers={[
              '',
              ...tableFields.map((item, index) => {
                return (
                  <TableCell key={index} width={item.width} onClick={() => {}}>
                    {item.label}
                    {item.sortable && item.sortKey && (
                      <SortButtons
                        isSorted={sortKey === item.sortKey}
                        order={order}
                        onChange={value =>
                          onSortingUpdate(item.sortKey ?? '', value)
                        }
                      />
                    )}
                  </TableCell>
                );
              }),
              '',
            ]}
            hoverable={false}
            rows={data?.map((pronunciation: IPronunciation, index: number) => ({
              key: index,
              columns: [
                <TableCell alignItems='center'>
                  <AudioPlayerIcon
                    isPlaying={playingId === pronunciation.id}
                    onClick={() =>
                      toggleAudio(pronunciation.id, pronunciation.audioS3Url)
                    }
                  />
                </TableCell>,
                ...tableFields.map((item, index) => {
                  let columnValue = <></>;
                  switch (item.value) {
                    case PronunciationsTableColumns.TEXT:
                      columnValue = (
                        <NormalText>{pronunciation.text}</NormalText>
                      );
                      break;
                    default:
                      columnValue = <></>;
                  }
                  return (
                    <TableCell key={index} width={item.width}>
                      {columnValue}
                    </TableCell>
                  );
                }),
                <TableCell justifyContent={'flex-end'}>
                  <Gap
                    flexDirection='row'
                    gap='8px'
                    alignItems='start'
                    justifyContent='end'
                  >
                    <Button
                      onClick={() => setPronunciationToDelete(pronunciation)}
                      size='small'
                      variant='destructive'
                      icon={
                        <MdDeleteForever
                          color={theme.colors.danger[100]}
                          size={20}
                        />
                      }
                    />
                  </Gap>
                </TableCell>,
              ],
              onClick: () => {},
            }))}
          />
        </div>
        <PaginationWrapper>
          <TableFooter>
            <TablePaginationNew />
            <TablePaginationSizeNew text={t('Show rows:')} />
          </TableFooter>
        </PaginationWrapper>
      </TableContextProvider>
    </Layout>
  );
};
